/* ──────────────────────────────────────────────────────────────────────────────
   Faz 4 — Satıcıya İade Detayı  ·  /vendor-returns/:id

   SALT-OKUNUR VARSAYILAN (#282): bu bir SAHA BELGESİDİR — plasiyerin kestiği,
   stoğu düşmüş, GİB'e gitmiş bir kayıt. Backoffice onu DÜZENLEMEZ; İPTAL eder
   ya da GİB'e YENİDEN GÖNDERİR. Sunucu da kesilmiş belgenin yeniden yazılmasını
   reddeder (ReturnsController.SaveWithDetails).

   Üç sekme:
     ① Kalemler + Alış Faturası Dağıtımı — İKİ KATMANLI: üstte ürün+toplam,
        altında hangi alış faturasına ne kadar (GİB zorunluluğu, spec §1).
        Σ dağıtım ≠ miktar ise satır KIRMIZI — belge tutarsızdır.
     ② Fotoğraflar — bozuk (SCRAP) iadenin KANITI. Hurda suistimale açık bir
        kalemdir; kanıtsız hurda denetime düşer (spec §10).
     ③ GİB Geçmişi — durum, hata mesajı, deneme sayısı, e-belge kimliği.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { StatusBadge } from './ui';
import { unwrapLookupItems } from './c360';
import { DetailShell, Section, Grid } from './detail/DetailShell';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  API_BASE, RETURN_STATUS, statusLabel, statusTone, subTypeLabel, subTypeTone, isScrap,
  gibLabel, damageReasonLabel, isVagueDamageReason, eDocumentLabel, stockTypeLabel,
  isPosted, isCancellable, allocationDiff, fmtDate, fmtDateTime, fmtQty, fmtMoney, unwrap,
} from './returnShared';

const muted = 'var(--text-secondary, #6b7280)';
const border = '1px solid var(--border-color, rgba(15,23,42,0.10))';

const st = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '8px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    color: muted, borderBottom: border,
  },
  td: { padding: '9px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-color, rgba(15,23,42,0.06))' },
  // Alış faturası dağıtımı — kalemin ALTINDA, girintili ikinci katman.
  tdAlloc: {
    padding: '6px 9px 6px 26px', whiteSpace: 'nowrap', fontSize: 12, color: muted,
    borderBottom: '1px dashed var(--border-color, rgba(15,23,42,0.08))',
    background: 'var(--bg-subtle, rgba(15,23,42,0.02))',
  },
  num: { textAlign: 'right' },
  mono: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 },
  danger: { color: 'var(--danger, #e2445c)', fontWeight: 800 },
  ok: { color: 'var(--success, #16a34a)', fontWeight: 700 },
  muted: { color: muted },
  empty: { padding: 12, fontSize: 13, color: muted },
  fact: { display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px', background: 'var(--bg-card, #fff)', border, borderRadius: 10 },
  factLabel: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.2, color: muted },
  factValue: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary, #0f172a)' },
  photoGrid: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  photo: {
    width: 132, height: 132, borderRadius: 10, objectFit: 'cover', border,
    background: 'var(--bg-subtle, rgba(15,23,42,0.04))', cursor: 'pointer',
  },
  photoMissing: {
    width: 132, height: 132, borderRadius: 10, border: '1px dashed var(--danger, #e2445c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
    fontSize: 11, fontWeight: 700, color: 'var(--danger, #e2445c)', padding: 8,
  },
  tlRow: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0' },
  tlDot: { width: 12, height: 12, borderRadius: 999, marginTop: 4, flexShrink: 0 },
  tlBody: { display: 'flex', flexDirection: 'column', gap: 2 },
  tlTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary, #0f172a)' },
  tlMeta: { fontSize: 12, color: muted },
};

function Fact({ label, value, tone }) {
  return (
    <div style={st.fact}>
      <span style={st.factLabel}>{label}</span>
      <span style={{ ...st.factValue, ...(tone === 'bad' ? st.danger : null) }}>{value ?? '-'}</span>
    </div>
  );
}

/** İptal / Tekrar gönder onay modalı — gerekçe iptal için ZORUNLU (denetim izi). */
function ConfirmModal({ title, body, danger, needReason, busy, confirmLabel, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const canSubmit = (!needReason || reason.trim().length > 0) && !busy;
  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h3 style={s.modalTitle}>{title}</h3>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>
        <div style={s.modalBody}>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: muted, lineHeight: 1.6 }}>{body}</p>
          {needReason && (
            <>
              <label style={s.label}>Gerekçe *</label>
              <textarea
                style={{ ...s.input, minHeight: 72, resize: 'vertical', width: '100%' }}
                value={reason}
                placeholder="Neden iptal ediliyor? (zorunlu — denetim izi)"
                onChange={(e) => setReason(e.target.value)}
              />
              {!reason.trim() && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--danger, #e2445c)' }}>
                  Gerekçe zorunludur.
                </div>
              )}
            </>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose} disabled={busy}>Vazgeç</button>
          <button
            style={{ ...(danger ? s.btnDanger : s.btnPrimary), opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
            disabled={!canSubmit}
            onClick={() => onSubmit(reason.trim())}
          >
            {busy ? 'İşleniyor…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function VendorReturnDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = usePageActions();
  usePageMeta('İade Detayı', 'Saha iade belgesi — kalemler, alış faturası dağıtımı, kanıt ve GİB durumu.');

  const [header, setHeader] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [tab, setTab] = useState('items');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'cancel' | 'resend'

  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/vendors/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([r, v, w]) => {
      setRepresentatives(unwrapLookupItems(r));
      setVendors(unwrapLookupItems(v));
      setWarehouses(unwrapLookupItems(w));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?id=${id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('İade belgesi alınamadı');
      const d = unwrap(await res.json());
      setHeader(d?.header || null);
      setItems(d?.items || []);
    } catch (e) {
      showToast('error', e.message || 'İade belgesi alınamadı');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const nameOf = (list, guid) => list.find((x) => x.id === guid)?.name || '-';
  const repName = header ? nameOf(representatives, header.representativeId) : '-';
  const vendorLabel = header ? nameOf(vendors, header.vendorId) : '-';
  const warehouseLabel = header
    ? (warehouses.find((x) => x.id === header.warehouseId)?.textId
      || warehouses.find((x) => x.id === header.warehouseId)?.name || '-')
    : '-';

  const scrap = isScrap(header?.returnSubType);
  const photoCount = useMemo(() => items.reduce((n, l) => n + (l.photos?.length || 0), 0), [items]);
  const totals = useMemo(() => ({
    quantity: items.reduce((n, l) => n + Number(l.quantity || 0), 0),
    lines: items.length,
  }), [items]);

  // Dağıtımı tutmayan satır = belge GİB'e eksik referansla gitmiş demektir (iş kuralı 2).
  const brokenLines = useMemo(
    () => items.filter((l) => Math.abs(allocationDiff(l).diff) > 0.0001),
    [items],
  );
  // Denetim: fotoğrafsız veya nedeni "diğer" olan bozuk satırlar.
  const vagueLines = useMemo(
    () => (scrap ? items.filter((l) => isVagueDamageReason(l.damageReason) || (l.photos?.length || 0) === 0) : []),
    [items, scrap],
  );

  const post = async (path, body, okMessage) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body || {}),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(payload?.message || payload?.Message || 'İşlem başarısız');
      showToast('success', okMessage);
      setConfirm(null);
      load();
    } catch (e) {
      showToast('error', e.message || 'İşlem başarısız');
    } finally {
      setBusy(false);
    }
  };

  const crumb = [
    { label: 'Satıcıya İade', to: '/vendor-returns' },
    { label: header?.returnNo || 'Detay', current: true },
  ];

  const rejected = Number(header?.status) === RETURN_STATUS.GIB_RED;
  const cancellable = header && isCancellable(header.status);
  const resendable = header && isPosted(header.status);

  const actionButtons = header ? (
    <>
      <button type="button" className="dps-btn" onClick={load} disabled={loading}>Yenile</button>
      {/* Muhasebe projeksiyonu — aynı Id'li Invoice(Type=3). Saha belgesi ≠ muhasebe belgesi. */}
      {header.invoiceId && (
        <button type="button" className="dps-btn" onClick={() => navigate(`/return-invoices/${header.invoiceId}`)}>
          Muhasebe Faturası
        </button>
      )}
      {canEdit && resendable && (
        <button type="button" className="dps-btn" onClick={() => setConfirm('resend')} disabled={busy}>
          GİB'e Tekrar Gönder
        </button>
      )}
      {canEdit && cancellable && (
        <button type="button" className="dps-btn" onClick={() => setConfirm('cancel')} disabled={busy}>
          Belgeyi İptal Et
        </button>
      )}
      <button type="button" className="dps-btn" onClick={() => navigate('/vendor-returns')}>Kapat</button>
    </>
  ) : null;

  const statusNode = header ? (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <StatusBadge tone={subTypeTone(header.returnSubType)}>{subTypeLabel(header.returnSubType)}</StatusBadge>
      <StatusBadge tone={statusTone(header.status)}>{statusLabel(header.status)}</StatusBadge>
      {brokenLines.length > 0 && <StatusBadge tone="bad">Dağıtım tutmuyor</StatusBadge>}
      {scrap && vagueLines.length > 0 && <StatusBadge tone="bad">Kanıt eksik</StatusBadge>}
    </span>
  ) : null;

  const tabs = [
    { key: 'items', label: 'Kalemler + Alış Faturası Dağıtımı', icon: 'list' },
    { key: 'photos', label: `Fotoğraflar${scrap ? ` (${photoCount})` : ''}`, icon: 'grid' },
    { key: 'gib', label: 'GİB Geçmişi', icon: 'shield' },
  ];

  // GİB zaman çizelgesi — yalnız GERÇEKTEN olan olaylar.
  const timeline = [];
  if (header) {
    timeline.push({
      color: '#94a3b8', title: 'Belge oluşturuldu (taslak)',
      meta: `${fmtDateTime(header.documentDate)} · ${repName}`,
    });
    if (header.sentAt || isPosted(header.status)) {
      timeline.push({
        color: 'var(--warn)', title: 'Belge kesildi — stok araçtan düştü, GİB kuyruğuna girdi',
        meta: `${fmtDateTime(header.sentAt || header.documentDate)} · ${eDocumentLabel(header.eDocumentType)}`
          + (header.eDocumentUuid ? ` · ${header.eDocumentUuid}` : ''),
      });
    }
    if (Number(header.gibRetryCount) > 0) {
      timeline.push({
        color: 'var(--accent)', title: `Tekrar gönderim: ${header.gibRetryCount} deneme`,
        meta: 'Backoffice kuyruğa geri koydu — stok bundan etkilenmez, mal zaten çıkmıştı.',
      });
    }
    if (Number(header.status) === RETURN_STATUS.GIB_KABUL) {
      timeline.push({ color: '#16a34a', title: 'GİB kabul etti — belge resmî', meta: header.eDocumentUuid || '' });
    }
    if (rejected) {
      timeline.push({
        color: 'var(--danger)', title: 'GİB reddetti — belge resmî DEĞİL, mal stoktan düşmüş durumda',
        meta: header.gibErrorMessage || 'Hata mesajı yok',
      });
    }
    if (Number(header.status) === RETURN_STATUS.IPTAL) {
      timeline.push({
        color: '#94a3b8', title: 'İptal edildi — projeksiyon faturası pasife çekildi, stok + cari geri alındı',
        meta: header.notes || '',
      });
    }
  }

  return (
    <>
      <DetailShell crumb={crumb} status={statusNode} tabs={tabs} activeTab={tab} onTab={setTab}
                   actions={actionButtons} viewActions={actionButtons} loading={loading} readOnly>
        {!loading && !header && <Section title="Bulunamadı"><div style={st.empty}>İade belgesi bulunamadı.</div></Section>}

        {header && (
          <>
            <Section icon="wallet" title="Belge"
                     subtitle="Saha belgesi (ReturnHeader). Muhasebe faturası aynı Id'li Invoice(Type=3) projeksiyonudur."
                     note={rejected
                       ? 'GİB bu belgeyi REDDETTİ. Belge resmî değil ama mal ARAÇTAN ÇIKMIŞTIR — GİB reddi stoğu geri almaz; düzeltme ayrı belgeyle yapılır.'
                       : undefined}>
              <Grid cols={3}>
                <Fact label="Belge No" value={header.returnNo} />
                <Fact label="Tarih" value={fmtDate(header.documentDate)} />
                <Fact label="Tip" value={subTypeLabel(header.returnSubType)} />
                <Fact label="Satıcı Cari" value={vendorLabel} />
                <Fact label="Plasiyer" value={repName} />
                <Fact label="Araç Deposu" value={warehouseLabel} />
                <Fact label="Durum" value={statusLabel(header.status)} tone={rejected ? 'bad' : undefined} />
                <Fact label="e-Belge Tipi" value={eDocumentLabel(header.eDocumentType)} />
                <Fact label="Kalem / Miktar" value={`${totals.lines} kalem · ${fmtQty(totals.quantity)}`} />
                <Fact label="Matrah" value={fmtMoney(header.vatBase)} />
                <Fact label="KDV" value={fmtMoney(header.vatAmount)} />
                <Fact label="Toplam" value={fmtMoney(header.netAmount)} />
                {header.notes && <Fact label="Not" value={header.notes} />}
              </Grid>
            </Section>

            {/* ① KALEMLER + ALIŞ FATURASI DAĞITIMI (iki katmanlı) */}
            {tab === 'items' && (
              <Section icon="list" title="Kalemler ve Alış Faturası Dağıtımı"
                       subtitle="GİB, iade edilen her malın hangi alış faturasıyla alındığını ister — dağıtım belgenin parçasıdır."
                       note={brokenLines.length > 0
                         ? `${brokenLines.length} satırda Σ dağıtım ≠ miktar. Belge eksik referansla kesilmiş; GİB reddi beklenir.`
                         : undefined}>
                {items.length === 0 ? (
                  <div style={st.empty}>Kalem yok.</div>
                ) : (
                  <table style={st.table}>
                    <thead>
                      <tr>
                        <th style={st.th}>Ürün</th>
                        <th style={st.th}>Birim</th>
                        <th style={st.th}>Stok Tipi</th>
                        <th style={st.th}>Parti / SKT</th>
                        {scrap && <th style={st.th}>Hasar Nedeni</th>}
                        <th style={{ ...st.th, ...st.num }}>Miktar</th>
                        <th style={{ ...st.th, ...st.num }}>Birim Fiyat</th>
                        <th style={{ ...st.th, ...st.num }}>Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((l) => {
                        const { qty, allocated, diff } = allocationDiff(l);
                        const cols = scrap ? 8 : 7;
                        return [
                          <tr key={l.id}>
                            <td style={st.td}>{l.productName || l.productCode || '-'}</td>
                            <td style={st.td}>{l.unit || '-'}</td>
                            <td style={st.td}>{stockTypeLabel(l.stockType)}</td>
                            <td style={st.td}>
                              {l.batchNo || '-'}{l.expiryDate ? ` · ${fmtDate(l.expiryDate)}` : ''}
                            </td>
                            {scrap && (
                              <td style={{ ...st.td, ...(isVagueDamageReason(l.damageReason) ? st.danger : null) }}>
                                {damageReasonLabel(l.damageReason)}
                              </td>
                            )}
                            <td style={{ ...st.td, ...st.num, fontWeight: 800 }}>{fmtQty(l.quantity)}</td>
                            {/* İade fiyatı ALIŞ fiyatıdır — plasiyer değiştiremez (iş kuralı 7). */}
                            <td style={{ ...st.td, ...st.num }}>{fmtMoney(l.unitPrice)}</td>
                            <td style={{ ...st.td, ...st.num }}>{fmtMoney(l.netAmount)}</td>
                          </tr>,
                          // İKİNCİ KATMAN — hangi alış faturasına ne kadar.
                          ...(l.allocations || []).map((a) => (
                            <tr key={`${l.id}-${a.id}`}>
                              <td style={st.tdAlloc} colSpan={cols - 3}>
                                <span style={st.mono}>{a.purchaseInvoiceNo || '(fatura no yok)'}</span>
                                {a.purchaseInvoiceDate ? ` · ${fmtDate(a.purchaseInvoiceDate)}` : ''}
                              </td>
                              <td style={{ ...st.tdAlloc, ...st.num }}>{fmtQty(a.quantity)}</td>
                              <td style={st.tdAlloc} colSpan={2} />
                            </tr>
                          )),
                          <tr key={`${l.id}-sum`}>
                            <td style={{ ...st.tdAlloc, ...(diff ? st.danger : st.ok) }} colSpan={cols}>
                              {diff === 0
                                ? `✓ Dağıtım tamam · ${fmtQty(allocated)} / ${fmtQty(qty)}`
                                : `⚠ ${fmtQty(qty)} girildi, ${fmtQty(allocated)} dağıtıldı — ${fmtQty(Math.abs(diff))} ${diff > 0 ? 'eksik' : 'fazla'}`}
                            </td>
                          </tr>,
                        ];
                      })}
                    </tbody>
                  </table>
                )}
              </Section>
            )}

            {/* ② FOTOĞRAFLAR — bozuk iade kanıtı */}
            {tab === 'photos' && (
              <Section icon="grid" title="Fotoğraflar"
                       subtitle={scrap
                         ? 'Bozuk iadenin kanıtı. Hurda suistimale açık bir kalemdir; kanıtsız hurda denetime düşer.'
                         : 'Sağlam iadede fotoğraf zorunlu değildir.'}
                       note={scrap && photoCount === 0
                         ? 'Bu bozuk iadede HİÇ FOTOĞRAF YOK. Sunucu kuralı (iş kuralı 5) en az 1 fotoğraf ister — belge kural devreye girmeden önce kesilmiş olabilir. Denetleyin.'
                         : undefined}>
                {!scrap ? (
                  <div style={st.empty}>Sağlam (ROBUST) iade — fotoğraf kanıtı aranmaz.</div>
                ) : items.length === 0 ? (
                  <div style={st.empty}>Kalem yok.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {items.map((l) => (
                      <div key={l.id}>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                          {l.productName || l.productCode || '-'} · {fmtQty(l.quantity)} {l.unit || ''}
                          {' · '}
                          <span style={isVagueDamageReason(l.damageReason) ? st.danger : st.muted}>
                            {damageReasonLabel(l.damageReason)}
                          </span>
                        </div>
                        <div style={st.photoGrid}>
                          {(l.photos || []).length === 0 ? (
                            <div style={st.photoMissing}>Fotoğraf yok<br />(kanıtsız hurda)</div>
                          ) : (
                            (l.photos || []).map((p) => (
                              <img key={p.id} src={p.url} alt="İade kanıtı" style={st.photo}
                                   title={`Yüklenme: ${fmtDateTime(p.uploadedAt)}`}
                                   onClick={() => p.url && window.open(p.url, '_blank', 'noopener')} />
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {/* ③ GİB GEÇMİŞİ */}
            {tab === 'gib' && (
              <Section icon="shield" title="GİB Geçmişi"
                       subtitle="Belge kesildiği an GİB kuyruğuna girer. Kabul/red oradan döner."
                       note={rejected
                         ? 'Reddedilen belge için iki yol var: hatayı düzeltip TEKRAR GÖNDER, ya da belgeyi İPTAL ET (projeksiyon pasife çekilir → stok ve cari geri alınır).'
                         : undefined}>
                <Grid cols={3}>
                  <Fact label="GİB Durumu" value={gibLabel(header.status)} tone={rejected ? 'bad' : undefined} />
                  <Fact label="Deneme Sayısı" value={header.gibRetryCount ?? 0} />
                  <Fact label="e-Belge Tipi" value={eDocumentLabel(header.eDocumentType)} />
                  <Fact label="e-Belge UUID" value={header.eDocumentUuid || '—'} />
                  <Fact label="Kesim Zamanı" value={header.sentAt ? fmtDateTime(header.sentAt) : '—'} />
                  <Fact label="Muhasebe Faturası" value={header.invoiceId ? 'Üretildi' : 'Üretilmedi'} />
                </Grid>

                {header.gibErrorMessage && (
                  <div style={{
                    marginTop: 12, padding: '12px 14px', borderRadius: 10,
                    background: 'var(--danger-soft, rgba(226, 68, 92,0.09))',
                    border: '1px solid var(--danger, #e2445c)',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--danger, #e2445c)' }}>
                      GİB Hata Mesajı
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>{header.gibErrorMessage}</div>
                  </div>
                )}

                <div style={{ marginTop: 14 }}>
                  {timeline.map((e, i) => (
                    <div key={i} style={{ ...st.tlRow, borderTop: i ? border : 'none' }}>
                      <span style={{ ...st.tlDot, background: e.color }} />
                      <span style={st.tlBody}>
                        <span style={st.tlTitle}>{e.title}</span>
                        {e.meta && <span style={st.tlMeta}>{e.meta}</span>}
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                  {canEdit && resendable && (
                    <button type="button" className="dps-btn" onClick={() => setConfirm('resend')} disabled={busy}>
                      GİB'e Tekrar Gönder
                    </button>
                  )}
                  {canEdit && cancellable && (
                    <button type="button" className="dps-btn" onClick={() => setConfirm('cancel')} disabled={busy}>
                      Belgeyi İptal Et
                    </button>
                  )}
                </div>
              </Section>
            )}
          </>
        )}
      </DetailShell>

      {confirm === 'cancel' && (
        <ConfirmModal
          title={`Belgeyi İptal Et — ${header?.returnNo || ''}`}
          body="İptal, projeksiyon faturasını PASİF'e çeker → stok ve cari etkisi otomatik geri alınır. Belge silinmez; iz kalır. Yalnız Taslak/Gönderildi durumundaki belge iptal edilebilir."
          danger needReason busy={busy} confirmLabel="Belgeyi İptal Et"
          onClose={() => setConfirm(null)}
          onSubmit={(reason) => post('cancel', { reason }, 'Belge iptal edildi — stok ve cari geri alındı.')}
        />
      )}
      {confirm === 'resend' && (
        <ConfirmModal
          title={`GİB'e Tekrar Gönder — ${header?.returnNo || ''}`}
          body="Belge GİB kuyruğuna geri konur, deneme sayısı artar ve önceki hata mesajı temizlenir. STOĞA DOKUNULMAZ — mal zaten araçtan çıkmıştı, yeniden gönderim fiziksel bir olay değildir."
          busy={busy} confirmLabel="Tekrar Gönder"
          onClose={() => setConfirm(null)}
          onSubmit={() => post('resend-gib', {}, 'Belge GİB kuyruğuna alındı.')}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

export default VendorReturnDetailPage;
